import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  CodeRangeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateLesserOfChargesMutation,
  useGetLesserOfChargesByIdQuery,
} from "store/queries/lesserOfCharges";

import {
  ILesserOfChargesFormData,
  INITIAL_LESSER_OF_CHARGES_FORM_DATA,
  LesserOfChargesForm,
} from "./components/LesserOfChargesForm";
import { groupedRulesetsForSubmit } from "./components/LesserOfChargesGroupedRules";

export function CopyLesserOfChargesPage() {
  useDocumentTitle("Copy Lesser-Of Charges Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetLesserOfChargesByIdQuery(
    id ?? ""
  );
  const [createMutation, { isLoading: isCreating }] =
    useCreateLesserOfChargesMutation();
  const [formData, setFormData] = useState<ILesserOfChargesFormData>(
    INITIAL_LESSER_OF_CHARGES_FORM_DATA
  );

  // A copy is a fresh create aimed at a new provider/payer, so it seeds as a
  // SCOPED rule: a SCOPED source carries its provider/payer over as a starting
  // point, while TYPICAL/FALLBACK sources have no scope to carry and start
  // empty. CLE-2524: the radio group stays editable here — this only seeds it.
  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    const sourceIsScoped = ruleset.applicability === "SCOPED";
    setFormData({
      scope: ruleset.scope,
      multiplier: Number(ruleset.multiplier),
      providerId: sourceIsScoped ? ruleset.providerId : null,
      payerId: sourceIsScoped ? ruleset.payerId : null,
      // The backend stores codes flat; fold consecutive runs back into ranges
      // so the editor shows "0272-0420" rather than 149 individual chips.
      codes: collapseToRanges(ruleset.codes ?? []),
      excludedCodes: collapseToRanges(ruleset.excludedCodes ?? []),
      groupName: ruleset.groupName ?? "",
      applicability: "SCOPED",
      // CLE-3058 — grouped rules name the source provider's case rates /
      // packages; a copy picks its own (the API drops them on copy too).
      groupedRulesets: [],
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!formData.scope) return;
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    let codes: string[];
    let excludedCodes: string[];
    try {
      codes = prepareCodesForSubmit(formData.codes);
      excludedCodes = prepareCodesForSubmit(formData.excludedCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createMutation({
        scope: formData.scope,
        multiplier: formData.multiplier,
        codes,
        excludedCodes,
        groupName: formData.groupName.trim() || null,
        // CLE-2524: applicability replaces the legacy isDefault flag.
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        // CLE-2597 — a FALLBACK rule may name a payer; only a TYPICAL template is scopeless.
        payerId: formData.applicability === "TYPICAL" ? null : formData.payerId,
        // CLE-3058 — [] clears saved groups (e.g. after switching to Total charges).
        groupedRulesets: groupedRulesetsForSubmit(
          formData.groupedRulesets,
          formData.scope,
          formData.applicability
        ),
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Lesser-Of charges rule copied successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to copy lesser-of charges rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <LesserOfChargesForm
      title="Copy Lesser-Of Charges Rule"
      submitLabel="Create Copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isCreating}
    />
  );
}
